import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, X, ArrowRightLeft, AlertCircle, Loader2, Check, PackagePlus, ArrowLeft, Search } from 'lucide-react';
import axios from 'axios';
import { ProductAutocomplete } from './forms/ProductAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const emptyLine = () => ({ key: Math.random(), product_id: '', _stock_count: null, _quantity: '', _items_confirmed: false, _assigned_item_ids: [], _rates: null });

export const InternalTransferCreate = () => {
  const navigate = useNavigate();
  const [transferNumber, setTransferNumber] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [fromCompanyId, setFromCompanyId] = useState('');
  const [toCompanyId, setToCompanyId] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [itemModal, setItemModal] = useState(null);
  const [itemModalAvailable, setItemModalAvailable] = useState([]);
  const [itemModalLoading, setItemModalLoading] = useState(false);
  const [itemModalSelected, setItemModalSelected] = useState([]);
  const [itemModalSearch, setItemModalSearch] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
      axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
    ]).then(([cRes, pRes]) => {
      setCompanies((cRes.data || []).filter(c => c.active !== false));
      setProducts(pRes.data || []);
    }).catch(() => setError('Failed to load master data'));
  }, []);

  // Re-fetch stock when fromCompany changes
  useEffect(() => {
    if (!fromCompanyId) return;
    lineItems.forEach((li, idx) => {
      if (!li.product_id) return;
      axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items?company_id=${fromCompanyId}`, getAuthHeaders())
        .then(res => setLineItems(prev => {
          const u = [...prev];
          if (u[idx]) u[idx] = { ...u[idx], _stock_count: res.data.stock_count, _items_confirmed: false, _assigned_item_ids: [] };
          return u;
        })).catch(() => {});
      axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/rates?company_id=${fromCompanyId}`, getAuthHeaders())
        .then(res => setLineItems(prev => {
          const u = [...prev];
          if (u[idx]) u[idx] = { ...u[idx], _rates: res.data };
          return u;
        })).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCompanyId]);

  const updateLine = (idx, value) => {
    setLineItems(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], product_id: value, _stock_count: null, _quantity: '', _items_confirmed: false, _assigned_item_ids: [], _rates: null };
      if (value && fromCompanyId) {
        axios.get(`${BACKEND_URL}/api/inventory/${value}/available-items?company_id=${fromCompanyId}`, getAuthHeaders())
          .then(res => setLineItems(p => { const c = [...p]; if (c[idx]) c[idx] = { ...c[idx], _stock_count: res.data.stock_count }; return c; }))
          .catch(() => {});
        axios.get(`${BACKEND_URL}/api/inventory/${value}/rates?company_id=${fromCompanyId}`, getAuthHeaders())
          .then(res => setLineItems(p => { const c = [...p]; if (c[idx]) c[idx] = { ...c[idx], _rates: res.data }; return c; }))
          .catch(() => {});
      }
      return u;
    });
  };

  const updateQuantity = (idx, value) => {
    setLineItems(prev => {
      const u = [...prev];
      if (!u[idx]) return prev;
      const stock = u[idx]._stock_count;
      let v = value === '' ? '' : Math.max(0, parseInt(value) || 0);
      if (v !== '' && stock !== null && v > stock) v = stock;
      // If quantity changes, clear any previously confirmed selection so user re-picks
      u[idx] = { ...u[idx], _quantity: v, _items_confirmed: false, _assigned_item_ids: [] };
      return u;
    });
  };

  const updateRate = (idx, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      if (!updated[idx]) return prev;
      updated[idx] = {
        ...updated[idx],
        _rates: { ...(updated[idx]._rates || {}), [field]: value },
      };
      return updated;
    });
  };

  const openItemModal = async (idx) => {
    const li = lineItems[idx];
    if (!li.product_id || !fromCompanyId) return;
    const qty = parseInt(li._quantity) || 0;
    if (!qty || qty <= 0) { setError(`Please enter a transfer quantity for the selected product first.`); return; }
    setError('');
    setItemModal({ idx, qty });
    setItemModalLoading(true);
    setItemModalSelected([...li._assigned_item_ids]);
    setItemModalSearch('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items?company_id=${fromCompanyId}`, getAuthHeaders());
      setItemModalAvailable(res.data.items || []);
    } catch { setItemModalAvailable([]); }
    finally { setItemModalLoading(false); }
  };

  const confirmItemModal = () => {
    const qty = itemModal?.qty || 0;
    if (itemModalSelected.length !== qty) return;
    setLineItems(prev => {
      const u = [...prev];
      u[itemModal.idx] = { ...u[itemModal.idx], _assigned_item_ids: [...itemModalSelected], _items_confirmed: true };
      return u;
    });
    setItemModal(null);
  };

  const toggleItem = (iid) => setItemModalSelected(prev => {
    if (prev.includes(iid)) return prev.filter(x => x !== iid);
    const qty = itemModal?.qty || 0;
    if (prev.length >= qty) return prev; // cap at qty
    return [...prev, iid];
  });

  const filteredModalItems = itemModalAvailable.filter(it => {
    if (!itemModalSearch) return true;
    const q = itemModalSearch.toLowerCase();
    return (it.item_id || '').toLowerCase().includes(q)
      || (it.asset_id || '').toLowerCase().includes(q)
      || (it.batch_number || '').toLowerCase().includes(q);
  });

  const allConfirmed = lineItems.filter(li => li.product_id).every(li => {
    if (li._stock_count === null) return true;
    if (li._stock_count === 0) return false;
    const qty = parseInt(li._quantity) || 0;
    if (!qty) return false;
    return li._items_confirmed && li._assigned_item_ids.length === qty;
  });

  const handleSave = async () => {
    setError('');
    if (!fromCompanyId) { setError('Please select a From Company'); return; }
    if (!toCompanyId) { setError('Please select a To Company'); return; }
    if (fromCompanyId === toCompanyId) { setError('From and To Company must be different'); return; }
    const valid = lineItems.filter(li => li.product_id && li._assigned_item_ids.length > 0);
    if (!valid.length) { setError('Please add at least one product with selected items'); return; }
    if (!allConfirmed) { setError('Some products have no stock or items not selected'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/internal-transfers`, {
        transfer_number: transferNumber || undefined,
        transfer_date: transferDate,
        from_company_id: fromCompanyId,
        to_company_id: toCompanyId,
        notes,
        line_items: valid.map(li => ({
          product_id: li.product_id,
          item_ids: li._assigned_item_ids,
          purchase_rate: Number(li._rates?.purchase_rate) || 0,
          display_rate: Number(li._rates?.display_rate) || 0,
          sale_rate: Number(li._rates?.sale_rate) || 0,
        })),
      }, getAuthHeaders());
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create transfer');
    } finally { setSubmitting(false); }
  };

  const productName = (pid) => products.find(p => p.id === pid)?.name || pid;
  const companyName = (cid) => companies.find(c => c.id === cid)?.company_name || '';

  if (success) {
    const t = success.transfer;
    const pb = success.purchase_bill;
    const si = success.sale_invoice;
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Internal Transfer Created</h2>
        <p className="text-slate-600 mb-8">{t.transfer_number} — Stock moved successfully</p>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 mb-6 text-left">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 text-center p-3 bg-white rounded border border-slate-200">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">From</p>
              <p className="font-bold text-slate-900">{t.from_company_name}</p>
            </div>
            <ArrowRightLeft className="h-6 w-6 text-orange-500 flex-shrink-0" />
            <div className="flex-1 text-center p-3 bg-white rounded border border-slate-200">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">To</p>
              <p className="font-bold text-slate-900">{t.to_company_name}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">{t.line_items?.length || 0} product(s) &nbsp;·&nbsp; Total: <strong>₹{(t.total||0).toLocaleString()}</strong></p>
        </div>
        {si && (
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-4 text-left">
            <p className="text-xs font-semibold text-green-700 uppercase mb-0.5">Auto-created Sale Invoice for {t.from_company_name}</p>
            <p className="font-mono font-bold text-green-900 text-lg">{si.invoice_number}</p>
          </div>
        )}
        {pb && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6 text-left">
            <p className="text-xs font-semibold text-blue-700 uppercase mb-0.5">Auto-created Purchase Bill for {t.to_company_name}</p>
            <p className="font-mono font-bold text-blue-900 text-lg">{pb.bill_number}</p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
          <button onClick={() => navigate('/admin/inventory/internal-transfers')} className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">All Transfers</button>
          <button onClick={() => navigate(`/admin/inventory/internal-transfers/${t.id}`)} className="px-5 py-2 bg-orange-500 text-white rounded-sm hover:bg-orange-600 font-bold uppercase tracking-wider">View Transfer</button>
          {si && <button onClick={() => navigate(`/admin/sale/invoices/${si.id}`)} className="px-5 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 font-bold uppercase tracking-wider">View Sale Invoice</button>}
          {pb && <button onClick={() => navigate(`/admin/purchase/bills/${pb.id}`)} className="px-5 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 font-bold uppercase tracking-wider">View Purchase Bill</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate('/admin/inventory/internal-transfers')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">Create Internal Transfer</h1>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Transfer Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Transfer Number</label>
            <input type="text" value={transferNumber} onChange={e => setTransferNumber(e.target.value)} placeholder="Auto-generated if blank"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Transfer Date</label>
            <input type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm" />
          </div>
        </div>
      </div>

      {/* Companies */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Company Route</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">From Company <span className="text-red-500">*</span></label>
            <select value={fromCompanyId} onChange={e => { setFromCompanyId(e.target.value); setLineItems([emptyLine()]); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white text-sm">
              <option value="">-- Select From Company --</option>
              {companies.map(c => <option key={c.id} value={c.id} disabled={c.id === toCompanyId}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">To Company <span className="text-red-500">*</span></label>
            <select value={toCompanyId} onChange={e => setToCompanyId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white text-sm">
              <option value="">-- Select To Company --</option>
              {companies.map(c => <option key={c.id} value={c.id} disabled={c.id === fromCompanyId}>{c.company_name}</option>)}
            </select>
          </div>
        </div>
        {fromCompanyId && toCompanyId && fromCompanyId !== toCompanyId && (
          <div className="flex items-center gap-3 mt-4 p-3 bg-orange-50 border border-orange-200 rounded-sm text-sm font-medium text-orange-800">
            <span>{companyName(fromCompanyId)}</span>
            <ArrowRightLeft className="h-4 w-4 text-orange-500" />
            <span>{companyName(toCompanyId)}</span>
          </div>
        )}
      </div>

      {/* Line Items */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Products to Transfer</h3>
        {!fromCompanyId ? (
          <p className="text-sm text-slate-400 italic">Select a From Company first.</p>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-2 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <div className="col-span-3">Product</div>
              <div className="col-span-3">Editable Rates (Purchase / Display / Sale)</div>
              <div className="col-span-1">Stock</div>
              <div className="col-span-2">Quantity</div>
              <div className="col-span-2">Items Selected</div>
              <div className="col-span-1"></div>
            </div>
            {lineItems.map((item, idx) => (
              <div key={item.key} className="mb-4">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <ProductAutocomplete
                      value={item.product_id}
                      onChange={(v) => updateLine(idx, v)}
                      products={products}
                      placeholder="Type to search product..."
                      testId={`it-product-${idx}`}
                    />
                  </div>
                  {/* Rates column */}
                  <div className="col-span-3">
                    {item.product_id && item._rates ? (
                      <div className="flex flex-col gap-1 text-xs">
                        {[
                          { field: 'purchase_rate', label: 'Purchase' },
                          { field: 'display_rate', label: 'Display' },
                          { field: 'sale_rate', label: 'Sale' },
                        ].map(rate => (
                          <label key={rate.field} className="grid grid-cols-[58px_1fr] items-center gap-1 text-slate-500">
                            <span>{rate.label}</span>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                              <input type="number" min="0" step="0.01"
                                value={item._rates[rate.field] ?? ''}
                                onChange={e => updateRate(idx, rate.field, e.target.value)}
                                data-testid={`it-${rate.field.replace('_rate', '')}-rate-${idx}`}
                                className="w-full pl-5 pr-2 py-1 border border-slate-200 rounded-sm text-xs text-slate-800 focus:border-orange-500 focus:outline-none" />
                            </div>
                          </label>
                        ))}
                        {item._rates.tax_name && (
                          <span className="text-slate-400">Tax: {item._rates.tax_name} ({item._rates.tax_percentage}%)</span>
                        )}
                        {(() => {
                          const qty = parseInt(item._quantity) || 0;
                          if (!qty) return null;
                          const sale = item._rates.sale_rate || item._rates.display_rate || item._rates.purchase_rate || 0;
                          const taxAmt = Math.round(sale * qty * (item._rates.tax_percentage || 0) / 100 * 100) / 100;
                          const total = Math.round((sale * qty + taxAmt) * 100) / 100;
                          return (
                            <span className="text-slate-700 font-bold mt-0.5">
                              Total: ₹{total.toLocaleString('en-IN')}
                              {taxAmt > 0 && <span className="font-normal text-slate-400 ml-1">(+₹{taxAmt} tax)</span>}
                            </span>
                          );
                        })()}
                      </div>
                    ) : item.product_id ? (
                      <span className="text-xs text-slate-400">Loading rates…</span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-sm">
                    {item.product_id && item._stock_count === null && <span className="text-slate-400 text-xs">…</span>}
                    {item.product_id && item._stock_count !== null && item._stock_count > 0 && <span className="text-green-600 font-medium text-xs">{item._stock_count}</span>}
                    {item.product_id && item._stock_count === 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">0</span>}
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="0" step="1" value={item._quantity}
                      max={item._stock_count || undefined}
                      disabled={!item.product_id || !item._stock_count}
                      onChange={e => updateQuantity(idx, e.target.value)}
                      placeholder={item._stock_count ? `Max ${item._stock_count}` : 'Qty'}
                      data-testid={`it-qty-${idx}`}
                      className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white disabled:bg-slate-50 disabled:text-slate-400" />
                  </div>
                  <div className="col-span-2 text-sm">
                    {item._items_confirmed
                      ? <span className="text-green-600 font-medium flex items-center gap-1 text-xs"><Check className="h-3.5 w-3.5" />{item._assigned_item_ids.length}/{item._quantity}</span>
                      : <span className="text-slate-400 text-xs">{item._quantity ? `0/${item._quantity}` : '-'}</span>}
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    {item.product_id && item._stock_count > 0 && (
                      <button type="button" onClick={() => openItemModal(idx)}
                        disabled={!parseInt(item._quantity)}
                        className={`p-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed ${item._items_confirmed ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}>
                        {item._items_confirmed ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                      </button>
                    )}
                    {lineItems.length > 1 && (
                      <button type="button" onClick={() => setLineItems(p => p.filter((_, i) => i !== idx))}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setLineItems(p => [...p, emptyLine()])}
              className="mt-2 inline-flex items-center gap-2 text-sm text-orange-500 hover:text-orange-600 font-medium">
              <Plus className="h-4 w-4" /> Add Another Product
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-3 justify-end">
        <button onClick={() => navigate('/admin/inventory/internal-transfers')}
          className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
        <button onClick={handleSave} disabled={submitting || !allConfirmed || !fromCompanyId || !toCompanyId}
          className="px-6 py-2.5 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? 'Creating…' : 'Create Internal Transfer'}
        </button>
      </div>

      {/* Item Selection Modal */}
      {itemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg flex flex-col" style={{ maxHeight: '80vh' }}>
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-900 uppercase">Select Items to Transfer</h3>
                <p className="text-xs text-slate-500 mt-0.5">{productName(lineItems[itemModal.idx]?.product_id)} — from {companyName(fromCompanyId)} &nbsp;·&nbsp; Pick exactly <span className="font-bold text-orange-600">{itemModal.qty}</span></p>
              </div>
              <button onClick={() => setItemModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-shrink-0 px-5 py-3 border-b border-slate-100 bg-slate-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={itemModalSearch}
                  onChange={e => setItemModalSearch(e.target.value)}
                  placeholder="Search item ID, asset ID or batch number…"
                  data-testid="item-modal-search"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {itemModalLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
              ) : filteredModalItems.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">{itemModalSearch ? 'No items match your search' : 'No available items in this company'}</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredModalItems.map(it => {
                    const checked = itemModalSelected.includes(it.item_id);
                    const atCap = !checked && itemModalSelected.length >= itemModal.qty;
                    return (
                      <label key={it.item_id} className={`flex items-center gap-3 px-5 py-3 ${atCap ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'bg-orange-50' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={checked} disabled={atCap} onChange={() => toggleItem(it.item_id)} className="w-4 h-4 accent-orange-500" />
                        <div className="flex-1">
                          <span className="font-mono text-sm text-slate-900">{it.item_id}</span>
                          <span className="text-xs font-medium text-slate-600 ml-2">Asset ID: <span className="font-mono">{it.asset_id || '-'}</span></span>
                          {it.batch_number && <span className="text-xs text-slate-400 ml-2">Batch: {it.batch_number}</span>}
                        </div>
                        {it.sale_rate && <span className="text-xs text-slate-500">₹{it.sale_rate}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 flex gap-3 justify-between items-center px-5 py-4 border-t border-slate-200 bg-white">
              <span className={`text-sm font-medium ${itemModalSelected.length === itemModal.qty ? 'text-green-600' : 'text-slate-500'}`}>
                {itemModalSelected.length} / {itemModal.qty} selected
              </span>
              <div className="flex gap-3">
                <button onClick={() => setItemModal(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm text-sm">Cancel</button>
                <button onClick={confirmItemModal} disabled={itemModalSelected.length !== itemModal.qty}
                  className="px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold disabled:opacity-50">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
